import React, { useEffect, useRef, useState } from "react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  LineChart, Line, CartesianGrid, Legend
} from "recharts";
import { ResponsiveHeatMap } from "@nivo/heatmap";
import Navbar from "./Navbar";
import Sidebar from "./Sidebar";
import axios from "axios";
import { useAuth } from "../AuthContext";
import iziToast from "izitoast";
import moment from "moment/moment";
import ExamAnalytics from "./ExamAnalytics";
import StudentAnalytics from "./StudentAnalytics";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";

// 📌 Mock API function (replace with real API calls)
const fetchAnalytics = async () => {
  return {
    topScore: 50,
    leastScore: 12,
    avgScore: 34,
    top10: [
      { rank: 1, name: "Sandeep Kumar", score: 50, accuracy: "100%", speed: "30s" },
      { rank: 2, name: "Amit", score: 48, accuracy: "96%", speed: "35s" },
      { rank: 3, name: "Ravi", score: 46, accuracy: "92%", speed: "33s" }
    ],
    subjectAvg: [
      { subject: "Hindi", avg: 8 },
      { subject: "Maths", avg: 7 },
      { subject: "English", avg: 9 },
    ],
    questionAvg: [
      { question: "Q1", avg: 0.8 },
      { question: "Q2", avg: 0.6 },
      { question: "Q3", avg: 0.9 },
      { question: "Q4", avg: 0.5 },
      { question: "Q5", avg: 1 },
    ],
    subjectComparison: [
      { id: "Hindi", data: [{ x: "Top", y: 95 }, { x: "Avg", y: 65 }, { x: "Least", y: 35 }] },
      { id: "Maths", data: [{ x: "Top", y: 88 }, { x: "Avg", y: 58 }, { x: "Least", y: 20 }] },
      { id: "English", data: [{ x: "Top", y: 92 }, { x: "Avg", y: 60 }, { x: "Least", y: 40 }] },
    ],
    questionComparison: [
      { id: "Q1", data: [{ x: "Top", y: 1 }, { x: "Avg", y: 0.6 }, { x: "Least", y: 0.2 }] },
      { id: "Q2", data: [{ x: "Top", y: 1 }, { x: "Avg", y: 0.7 }, { x: "Least", y: 0.3 }] },
      { id: "Q3", data: [{ x: "Top", y: 1 }, { x: "Avg", y: 0.8 }, { x: "Least", y: 0.4 }] },
    ],
    bestSubject: "English",
    worstSubject: "Maths",
    correctIncorrect: [
      { question: "Q1", correct: 45, incorrect: 5 },
      { question: "Q2", correct: 40, incorrect: 10 },
      { question: "Q3", correct: 47, incorrect: 3 },
      { question: "Q4", correct: 30, incorrect: 20 },
      { question: "Q5", correct: 50, incorrect: 0 },
    ],
    timeSubject: [
      { subject: "Hindi", time: 60 },
      { subject: "Maths", time: 120 },
      { subject: "English", time: 80 },
    ],
    timeQuestion: [
      { question: "Q1", time: 33 },
      { question: "Q2", time: 14 },
      { question: "Q3", time: 34 },
      { question: "Q4", time: 178 },
      { question: "Q5", time: 106 },
    ]
  };
};

const Analytics = () => {
  const divRef = useRef();
  const { apipath, logout, wspath } = useAuth();
  const token = localStorage.getItem('token');
  const [data, setData] = useState(null);
  const [organizations, setOrganizations] = useState([]);
  const [exams, setExams] = useState([]);
  const [tests, setTests] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [students, setStudents] = useState([]);
  const [selectedUser, setSelectedUser] = useState('');
  const [selectedExam, setSelectedExam] = useState('');
  const [selectedTest, setSelectedTest] = useState('');
  const [selectedSchedule, setSelectedSchedule] = useState('');
  const [selectedStudent, setselectedStudent] = useState('');

  const exportExcel = async (response, fileName = 'report.xlsx') => {
    const workbook = new ExcelJS.Workbook();

    // ============================
    // 1. Summary / scalar values
    // ============================
    const summarySheet = workbook.addWorksheet("Summary");

    const summaryData = [];
    for (const [key, value] of Object.entries(response)) {
      if (!Array.isArray(value) && typeof value !== "object") {
        summaryData.push({ key, value });
      }
    }

    summarySheet.columns = [
      { header: "Field", key: "key", width: 25 },
      { header: "Value", key: "value", width: 40 },
    ];
    summarySheet.addRows(summaryData);

    summarySheet.getRow(1).font = { bold: true };

    // ============================
    // 2. Nested Objects (best, worst, etc.)
    // ============================
    Object.entries(response).forEach(([key, value]) => {
      if (typeof value === "object" && !Array.isArray(value) && value !== null) {
        const sheet = workbook.addWorksheet(key);

        sheet.columns = Object.keys(value).map((k) => ({
          header: k,
          key: k,
          width: 20,
        }));

        sheet.addRow(value);
        sheet.getRow(1).font = { bold: true };
      }
    });

    // ============================
    // 3. Arrays (subjectData, questionWise, etc.)
    // ============================
    Object.entries(response).forEach(([key, value]) => {
      if (Array.isArray(value) && value.length > 0) {
        const sheet = workbook.addWorksheet(key);

        // Dynamically get all possible keys (union of keys across all rows)
        const allKeys = Array.from(new Set(value.flatMap((item) => Object.keys(item))));

        sheet.columns = allKeys.map((k) => ({
          header: k,
          key: k,
          width: 20,
        }));

        sheet.addRows(value);
        sheet.getRow(1).font = { bold: true };
      }
    });

    // ============================
    // Export the file
    // ============================
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    saveAs(blob, fileName);
  }

  const downloadPDF = async () => {
    const element = divRef.current;
    if (!element) return null;
    const canvas = await html2canvas(element, { scale: 2 });
    const imgData = canvas.toDataURL("image/png");

    // Get element dimensions in px
    const imgWidthPx = canvas.width;
    const imgHeightPx = canvas.height;

    // Convert px -> mm (A4: 210 x 297 mm)
    const pxToMm = 0.264583; // 1px = 0.264583mm
    const pdfWidth = imgWidthPx * pxToMm;
    const pdfHeight = imgHeightPx * pxToMm;

    // Create custom size PDF
    const pdf = new jsPDF("p", "mm", [pdfWidth, pdfHeight]);

    pdf.addImage(imgData, "PNG", 0, 0, pdfWidth, pdfHeight);
    pdf.save("single-page.pdf");
  };
  const getAnalytics = async () => {
    // try {
    setData(null);
    setselectedStudent("");
    const response = await axios.post(apipath + '/exam_analytics', { 'exam_test_id': selectedTest, 'schedule_id': selectedSchedule }, {
      headers: {
        Authorization: 'Bearer ' + token
      }
    });

    console.log(response.data);
    if (response.data.status === "false") {
      iziToast.error({
        message: response.data.message,
        position: "topCenter"
      });
      setData(null);
      setStudents([]);
      return false;
    }
    else {
      setData(response.data.data);
      setStudents(response.data.students);
    }
    // } catch (e) {
    //   console.log(e);
    // }
  }

  const getStudentAnalytics = async (e) => {
    // try {
    setData(null);
    const studentval = e.target.value;
    if (studentval === '') return null;
    const response = await axios.post(apipath + '/student_analytics', { 'id': studentval }, {
      headers: {
        Authorization: 'Bearer ' + token
      }
    });

    console.log(response.data);
    if (response.data.status === "false") {
      iziToast.error({
        message: response.data.message,
        position: "topCenter"
      });
      setData(null);
      setselectedStudent(studentval);
      // setStudents([]);
      return false;
    }
    else {
      setData(response.data);
      setselectedStudent(studentval);
      // setStudents(response.data.students);
    }
    // } catch (e) {
    //   console.log(e);
    // }
  }

  useEffect(() => {
    // fetchAnalytics().then(setData);
    if (selectedSchedule !== '') {
      getAnalytics();
    }
  }, [selectedSchedule]);


  useEffect(() => {
    const getOrganizations = async () => {
      try {
        const response = await axios.post(
          apipath + '/organizations',
          {},
          {
            headers: {
              Authorization: 'Bearer ' + token
            }
          }
        );
        // console.log(response.data);
        if (response.data.status === "unauthentic") {
          iziToast.error({
            'message': response.data.message,
            'position': 'topCenter'
          });
          logout();
        }
        else if (response.data.status === "false") {
          iziToast.error({
            'message': response.data.message,
            'position': 'topCenter'
          });
          return false;
        }
        setOrganizations(response.data.organizations);
      }
      catch (error) {
        // console.error('Error fetching data:', error);
        if (error.status === 401) {
          iziToast.error({
            message: "Un-Authorised! Please Login.",
            position: "topCenter"
          });
          logout();
        }
        else {
          iziToast.error({
            message: error,
            position: 'topCenter'
          });
        }

      } finally {

      }
    }
    getOrganizations();
  }, []);

  const getExams = async () => {
    try {
      console.log(selectedUser);
      const response = await axios.post(
        apipath + '/exams',
        { 'id': selectedUser },
        {
          headers: {
            Authorization: 'Bearer ' + token
          }
        }
      );
      // console.log(response.data);
      if (response.data.status === "unauthentic") {
        iziToast.error({
          message: response.data.message,
          position: "topCenter"
        });
        logout();
      }
      if (response.data.status === "false") {
        iziToast.error({
          message: response.data.message,
          position: "topCenter"
        });
        return false;
      }
      setExams(response.data.exams);
    }
    catch (error) {
      // console.error('Error fetching data:', error);
      if (error.status === 401) {
        iziToast.error({
          message: "Un-Authorised! Please Login.",
          position: "topCenter"
        });
        logout();
      }
      else {
        iziToast.error({
          message: error,
          position: 'topCenter'
        });
      }

    } finally {

    }
  }
  useEffect(() => {
    setSelectedExam("");
    setSelectedTest("");
    setSelectedSchedule("");
    setselectedStudent("");
    getExams();
  }, [selectedUser]);


  const getTests = async () => {
    try {
      if (selectedExam) {
        const response = await axios.post(
          apipath + '/tests',
          { 'examid': selectedExam },
          {
            headers: {
              Authorization: 'Bearer ' + token
            }
          }
        );
        // console.log(response.data);
        if (response.data.status === "unauthentic") {
          iziToast.error({
            message: response.data.message,
            position: "topCenter"
          });
          logout();
        }
        if (response.data.status === "false") {
          iziToast.error({
            message: response.data.message,
            position: "topCenter"
          });
          return false;
        }
        setTests(response.data.tests);
      }
      else {
        setTests([]);
      }
    }
    catch (error) {
      // console.error('Error fetching data:', error);
      if (error.status === 401) {
        iziToast.error({
          message: "Un-Authorised! Please Login.",
          position: "topCenter"
        });
        logout();
      }
      else {
        iziToast.error({
          message: error,
          position: 'topCenter'
        });
      }

    } finally {

    }
  }
  useEffect(() => {
    setSelectedTest("");
    setSelectedSchedule("");
    setselectedStudent("");
    getTests();
  }, [selectedExam]);

  const getSchedules = async () => {
    try {
      if (selectedTest) {
        const response = await axios.post(
          apipath + '/test_schedules',
          { 'test_id': selectedTest },
          {
            headers: {
              Authorization: 'Bearer ' + token
            }
          }
        );
        // console.log(response.data);
        if (response.data.status === "unauthentic") {
          iziToast.error({
            message: response.data.message,
            position: "topCenter"
          });
          logout();
        }
        if (response.data.status === "false") {
          iziToast.error({
            message: response.data.message,
            position: "topCenter"
          });
          return false;
        }
        setSchedules(response.data.tests);
      }
      else {
        setSchedules([]);
      }
    }
    catch (error) {
      // console.error('Error fetching data:', error);
      if (error.status === 401) {
        iziToast.error({
          message: "Un-Authorised! Please Login.",
          position: "topCenter"
        });
        logout();
      }
      else {
        iziToast.error({
          message: error,
          position: 'topCenter'
        });
      }

    } finally {

    }
  }
  useEffect(() => {
    setselectedStudent("");
    setSelectedSchedule("");
    getSchedules();
  }, [selectedTest]);


  // if (!data) return <p className="text-center mt-10">No Data....</p>;

  return (
    <>
      {<Navbar />}
      <div className="flex flex-col md:flex-row">
        {<Sidebar activePage={"analytics"} />}
        <div className="main-content flex-1 bg-gray-100 pb-24 md:pb-5 w-full md:w-4/5 lg:w-5/6 pt-16 md:pt-8">

          <div className="bg-teal-700 pt-4">
            <div className="rounded-tl-3xl bg-teal-600 p-2 shadow text-2xl text-white">
              <h3 className="font-bold pl-2">Analytics</h3>
            </div>
          </div>
          <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="col-span-2 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 p-2 gap-4 ">
              <select className="p-2 border border-gray-300 rounded-lg shadow w-full" value={selectedUser} onChange={(e) => setSelectedUser(e.target.value)}>
                <option value={""}>Select Organization</option>
                {organizations.map((org) => {
                  return (<>
                    <option value={org.id}>{org.name}</option>
                  </>)
                })}
              </select>
              <select className="p-2 border border-gray-300 rounded-lg shadow w-full" value={selectedExam} onChange={(e) => setSelectedExam(e.target.value)}>
                <option value={""}>Select Exam</option>
                {exams.map((exm) => {
                  return (<>
                    <option value={exm.id}>{exm.exam_name}</option>
                  </>)
                })}
              </select>
              <select className="p-2 border border-gray-300 rounded-lg shadow w-full" value={selectedTest} onChange={(e) => setSelectedTest(e.target.value)}>
                <option value={""}>Select Test</option>
                {tests.map((tst) => {
                  return (<>
                    <option value={tst.id}>{tst.test_name}</option>
                  </>)
                })}
              </select>
              <select className="p-2 border border-gray-300 rounded-lg shadow w-full" value={selectedSchedule} onChange={(e) => setSelectedSchedule(e.target.value)}>
                <option value={""}>Select Schedule</option>
                <option value={"All"}>All</option>
                {schedules.map((sch) => {
                  return (<>
                    <option value={sch.id}>{moment(sch.start_time).format("YYYY-MM-DD HH:mm:ss")}</option>
                  </>)
                })}
              </select>
              <select className="p-2 border border-gray-300 rounded-lg shadow w-full" value={selectedStudent} onChange={(e) => getStudentAnalytics(e)}>
                <option value={""}>Select Student</option>
                {students.map((std) => {
                  return (<>
                    <option value={std.id}>{std.name}({std.roll_no})</option>
                  </>)
                })}
              </select>
            </div>
            <div className="col-span-2 flex justify-end gap-2">
              <button
                onClick={downloadPDF}
                className="px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-lg"
              >
                Download PDF
              </button>
              <button
                onClick={()=>exportExcel(data)}
                className="px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-lg"
              >
                Download Excel
              </button>
            </div>
            {!data ? <></> : <div ref={divRef} className="col-span-2 grid grid-cols-2 gap-4 bg-gray-100 p-2">
              {selectedStudent !== '' ? <><StudentAnalytics data={data} /></> : <>
                <ExamAnalytics edata={data} />
              </>
              }
            </div>}
          </div>
        </div>
      </div>
    </>
  );
};

export default Analytics;
